import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useAxios } from '../utils/axiosApi';
import { userSrl } from '../utils/formatAppLib';
import { CreditType } from '../utils/shared/business';
import { useUser, useAllUsers } from '../utils/users';

const useStyles = makeStyles((theme) => ({
  formField: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  confirmText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
}));

// Keep in sync with api/libs/airtableLib.js
const LEDGER_REASONS = [
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'R&D', label: 'R&D' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Support', label: 'Support' },
  { value: 'Training', label: 'Training' },
];

export default function ServiceCreditsModal(props) {
  const {
    open,
    onClose,
    onSuccess,
    userId,
    userEmail,
    defaultCreditType = CreditType.SERVICE_CREDIT,
    currentUserCredits = 0,
    currentServiceCredits = 0,
  } = props;

  const classes = useStyles();
  const { apiPatch } = useAxios();

  // Get mutation functions for refreshing user data after credit change
  const { mutate: mutateGalleryUser } = useUser(userId);
  const { mutate: mutateAllUsers } = useAllUsers();

  const [creditType, setCreditType] = useState(defaultCreditType);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('Support');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = () => {
    if (!userId) {
      setError('No user selected');
      return;
    }
    const parsedAmount = parseInt(amount);
    if (isNaN(parsedAmount) || parsedAmount === 0) {
      setError('Please enter a valid non-zero credit amount');
      return;
    }
    if (!reason) {
      setError('Please select a reason');
      return;
    }
    if (!notes.trim()) {
      setError('Please enter notes explaining this change');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError(null);

    try {
      const bodyData = creditType === CreditType.SERVICE_CREDIT
        ? { deltaServiceCredits: parseInt(amount), reason, notes }
        : { deltaUserCredits: parseInt(amount), reason, notes };

      const response = await apiPatch(
        '/account',
        bodyData,
        { params: { userId: userId } }
      );

      if (response.data?.status === 'success') {
        const { creditChange } = response.data.data;
        // Reset form
        setAmount('');
        setReason('Support');
        setNotes('');
        setError(null);
        setSuccess(true);

        // Refresh both the individual user data and the allUsers list
        mutateGalleryUser();
        mutateAllUsers();

        // Call onSuccess callback to trigger user data refresh
        if (onSuccess) {
          onSuccess(creditChange);
        }

        // Close modal after short delay to show success message
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      } else {
        setError(response.data?.message || 'Failed to update service credits');
      }
    } catch (err) {
      console.error('Error updating service credits:', err);
      setError(
        err.response?.data?.message ||
        'An error occurred while updating service credits'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAmount('');
      setReason('Support');
      setNotes('');
      setError(null);
      setSuccess(false);
      setShowConfirm(false);
      onClose();
    }
  };

  // Update credit type when defaultCreditType changes
  useEffect(() => {
    setCreditType(defaultCreditType);
  }, [defaultCreditType]);

  const parsedAmount = parseInt(amount);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount !== 0;

  // Format userId as SRL (Shopfront Resource Locator)
  const userSrlFormatted = userId ? userSrl(userId) : '';

  return (
    <>
      <Dialog open={open && !showConfirm} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Credits</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add or remove credits from a customer&apos;s account.
          </DialogContentText>

          {userId && (
            <div className={classes.formField}>
              {userEmail && (
                <Typography variant="body2" color="textSecondary">
                  <strong>Email:</strong> {userEmail}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                <strong>User ID:</strong> {userSrlFormatted}
              </Typography>
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
            </div>
          ) : success ? (
            <Alert severity="success" className={classes.formField} style={{ marginTop: '100px', marginBottom: '100px' }}>
              Credits updated successfully!
            </Alert>
          ) : (
            <>
              <FormControl fullWidth variant="outlined" className={classes.formField}>
                <InputLabel id="credit-type-label">Credit Type</InputLabel>
                <Select
                  labelId="credit-type-label"
                  value={creditType}
                  onChange={(e) => setCreditType(e.target.value)}
                  disabled={loading}
                  label="Credit Type"
                >
                  <MenuItem value={CreditType.USER_CREDIT}>User Credits</MenuItem>
                  <MenuItem value={CreditType.SERVICE_CREDIT}>Service Credits</MenuItem>
                </Select>
              </FormControl>

              <Alert severity="info" className={classes.formField}>
                <strong>Current Balance:</strong> {creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits} {creditType === CreditType.SERVICE_CREDIT ? 'service' : 'user'} credit{(creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits) !== 1 ? 's' : ''}
                <br />
                <strong>New Balance:</strong> {isValidAmount ? (creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits) + parsedAmount : (creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits)} {creditType === CreditType.SERVICE_CREDIT ? 'service' : 'user'} credit{(isValidAmount ? ((creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits) + parsedAmount) : (creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits)) !== 1 ? 's' : ''}
              </Alert>

              {!userId && (
                <Alert severity="warning" className={classes.formField}>
                  No user selected. Please select a user from the gallery first.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Credits Amount"
                variant="outlined"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading}
                className={classes.formField}
                placeholder="e.g., 10 to add, -5 to remove"
                helperText="Use positive numbers to add, negative to remove"
              />

              <FormControl fullWidth variant="outlined" className={classes.formField}>
                <InputLabel id="reason-label">Reason</InputLabel>
                <Select
                  labelId="reason-label"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={loading}
                  label="Reason"
                >
                  {LEDGER_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Notes"
                variant="outlined"
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
                className={classes.formField}
                placeholder="Explain the reason for this credit change..."
                helperText="Required: Provide details about why these credits are being added/removed"
              />
            </>
          )}

          {error && (
            <Alert severity="error" className={classes.formField}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading || !userId || !isValidAmount || !reason || !notes.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Credits Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please confirm the following credits change:
          </DialogContentText>
          <Typography className={classes.confirmText}>
            {parsedAmount > 0 ? 'Add' : 'Remove'} <strong>{Math.abs(parsedAmount)}</strong>{' '}
            {creditType === CreditType.SERVICE_CREDIT ? 'service' : 'user'} credit{Math.abs(parsedAmount) !== 1 ? 's' : ''}{' '}
            {parsedAmount > 0 ? 'to' : 'from'} user:
          </Typography>
          <Typography variant="body2" style={{ fontFamily: 'monospace', marginBottom: 16 }}>
            {userEmail || userId} ({userSrlFormatted})
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            <strong>Current Balance:</strong> {creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            <strong>New Balance:</strong> {(creditType === CreditType.SERVICE_CREDIT ? currentServiceCredits : currentUserCredits) + parsedAmount}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Reason:</strong> {reason}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Notes:</strong> {notes}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
